import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  Stripe,
  StripeElements,
  StripeElementsOptions,
  StripeExpressCheckoutElementConfirmEvent,
} from "@stripe/stripe-js";
import { Shield, Lock, CreditCard } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation, Trans } from "react-i18next";
import { cn } from "@/lib/utils";
import { BookingApiError } from "@/lib/bookings-api";
import { toRealError } from "@/lib/normalize-error";
import { CHECKOUT_STATUS_UNAVAILABLE_MESSAGE, isCheckoutStatusUnavailable } from "@workspace/booking-rules";
import { track } from "@/lib/analytics";
import type { RenterEvents } from "@/lib/analytics/events";
import * as Sentry from "@sentry/react";
import { useNativeBridge, type NativeBridge } from "@/lib/native-bridge";
import visaLogo from "@/assets/payment/visa.svg";
import mastercardLogo from "@/assets/payment/mastercard.svg";
import amexLogo from "@/assets/payment/amex.svg";
import applePayLogo from "@/assets/payment/apple-pay.svg";
import googlePayLogo from "@/assets/payment/google-pay.svg";
import { sanitizeCheckoutUrl } from "@/lib/checkout-state";
import { isOccasionSelectionError } from "@/lib/checkout-occasion";
import { depositCopyKey, type DepositCopyVariant } from "@/lib/checkout-deposit-copy";
import { CheckoutContractChangedError, type CheckoutCaptureMethod } from "@/lib/checkout-payment-contract";
import { checkoutDeclineMessage } from "@/lib/checkout-decline";

// The immutable contract belongs to this mounted Elements instance, not to
// a later render's quote or an in-flight server response.
const ElementsCaptureContext = createContext<CheckoutCaptureMethod | undefined>(undefined);

export type SavedCardLite = {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault?: boolean;
};

export type PrepareBookingResult = {
  clientSecret: string;
  publicId: string;
  /** H3 — authoritative total returned by the booking server so the client
   *  can compare it against the displayed quote BEFORE confirming the Stripe
   *  charge. Null when the server response did not include a quote (e.g. the
   *  no-payment-required path or legacy fallback). */
  serverTotal?: number | null;
  captureMethod?: CheckoutCaptureMethod;
};

/** Task #4687 — classifies a payment-section error so the page banner can use
 *  calm, accurate framing.
 *  - "charge_failed": a genuine charge problem (decline, SCA failure, server
 *    error). Keeps the red "Payment Failed" header.
 *  - "card_validation": a client-side card-field problem reported by
 *    elements.submit() (e.g. incomplete card number after the Elements were
 *    remounted by a moderation block). Surfaces a calm "check your card
 *    details" header instead of implying the charge was attempted and failed. */
export type PaymentErrorKind = "charge_failed" | "card_validation" | "location_verification" | "status_unavailable";

export function isPickupPolicyError(error: unknown): boolean {
  return error instanceof BookingApiError
    && ["pickup_state_unknown", "pickup_in_ny", "ny_registered_vehicle"]
      .includes(String(error.code || error.body?.code || error.body?.error));
}

/** Task #4687 — the page-level payment error, carrying both the message and
 *  its classification so the banner can pick the right (calm vs alarming)
 *  header. `null` clears the banner. */
export type PaymentError = { message: string; kind: PaymentErrorKind };

function isAvailabilityConflictError(error: unknown): boolean {
  return error instanceof BookingApiError && (
    ["dates_unavailable", "dates_conflict_hold", "dates_conflict_renter_own"].includes(String(error.code))
    || /dates?\s+conflict|some dates unavailable/i.test(String(error.message || error.body?.message || ""))
  );
}

/**
 * A resolved Stripe confirmation error is not necessarily a failed payment.
 * Transport/API errors mean the browser did not learn whether Stripe received
 * confirmation, so they must take the same reconciliation path as a timeout
 * or rejected promise. Only Stripe's explicit card/wallet failure shape may
 * return the renter to an immediate retry (and release its uncharged PFC hold).
 */
function isExplicitTerminalStripeConfirmationError(error: unknown): error is { message?: string; code?: string; decline_code?: string } {
  return Boolean(
    error &&
      typeof error === "object" &&
      (error as { type?: unknown }).type === "card_error",
  );
}

export type WalletAnalyticsContext = Omit<
  RenterEvents["checkout_wallet_lifecycle"],
  "stage" | "outcome" | "wallet_capability" | "failure_category"
>;
export type WalletLifecycleUpdate = Pick<
  RenterEvents["checkout_wallet_lifecycle"],
  "stage" | "outcome" | "wallet_capability" | "failure_category"
>;
type PaymentAnalyticsContext = { checkout_journey_id: string };
type PaymentLifecycleUpdate = Omit<RenterEvents["checkout_payment_lifecycle"], "checkout_journey_id" | "attempt_id" | "method">;

function StripeBadge() {
  return (
    <svg className="h-[14px]" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Stripe">
      <path d="M60 12.5C60 5.596 53.731 0 46.154 0H13.846C6.269 0 0 5.596 0 12.5S6.269 25 13.846 25h32.308C53.731 25 60 19.404 60 12.5Z" fill="#635BFF"/>
      <path d="M27.44 10.27c0-.79.65-1.1 1.72-1.1.55 0 1.24.11 1.93.39V7.42c-.65-.26-1.29-.36-1.93-.36-2.07 0-3.45 1.08-3.45 2.89 0 2.82 3.88 2.37 3.88 3.58 0 .93-.82 1.23-1.96 1.23-.85 0-1.83-.35-2.36-.65v2.17c.8.35 1.61.49 2.36.49 2.12 0 3.57-1.05 3.57-2.88-.01-3.04-3.9-2.5-3.9-3.62h.14Zm8.22-3.18-1.7.36v1.99h-1.14v2.01h1.14v3.12c0 1.59.77 2.42 2.36 2.42.65 0 1.14-.14 1.43-.3v-1.96c-.23.09-.98.09-1.31.09-.45 0-.78-.18-.78-.87v-2.5h2.09V9.44h-2.09V7.09Zm5.72 1.51a1.09 1.09 0 0 0-1.09 1.11c0 .62.48 1.11 1.09 1.11.62 0 1.1-.49 1.1-1.11 0-.61-.48-1.11-1.1-1.11ZM40.5 17h2.01V9.44H40.5V17Zm5.66-10.26h-2.01V17h2.01v-3.37c0-1.2.52-1.75 1.41-1.75.27 0 .59.05.85.13l.01-1.96c-.23-.07-.51-.12-.81-.12-.98 0-1.6.56-1.84 1.12l-.11-.61h-1.51v7.56Zm8.53 2.69c0-.82-.39-1.47-1.3-1.47-.6 0-1.12.36-1.39.89l-.03-.77h-1.68V17h2.01v-4.2c.35-.46.84-.6 1.18-.6.49 0 .67.28.67.82V17h2.01v-4.31c0-1.53-.69-3.26-2.47-3.26l1 .7Z" fill="#fff"/>
    </svg>
  );
}

export function StripeFooter() {
  const { t } = useTranslation();
  return (
    <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
      <Lock className="h-3 w-3" />
      <span>{t("checkout.powered_by")}</span>
      <StripeBadge />
      <span>{t("checkout.encryption_label")}</span>
    </div>
  );
}

/**
 * Static trust-strip of accepted-payment brand marks.
 *
 * Note: this row is a *decorative trust badge* — it renders before Stripe
 * Elements has loaded and is intentionally hardcoded to the brands the
 * Stripe account is configured to accept on this platform (Visa,
 * Mastercard, Amex, Apple Pay, Google Pay). The actual checkout selector
 * — <ExpressCheckoutElement> + <PaymentElement> below — pulls live from
 * Stripe and only renders the methods enabled for the renter's region in
 * the Stripe Dashboard. Adding/removing a method in Stripe will *not*
 * change this row; update PAYMENT_BRANDS below if the supported brands ever
 * change.
 *
 * Each badge is the official brand logo, imported from src/assets so Vite
 * fingerprints + long-caches it at build time (small marks are inlined as
 * data URIs, larger ones emitted as hashed files). They render crisp at any
 * DPR; per-brand heights are tuned so the differently proportioned marks
 * share a uniform optical size inside the pills.
 */
/* --------- card-brand marks (official brand logos, bundled as assets) --------- */

const PILL_BASE =
  "inline-flex h-[26px] items-center justify-center rounded-[4px] bg-white px-2 ring-1 ring-black/[0.12] shadow-sm";

const PAYMENT_BRANDS: ReadonlyArray<{ src: string; label: string; className: string }> = [
  { src: visaLogo, label: "Visa", className: "h-[12px] w-auto" },
  { src: mastercardLogo, label: "Mastercard", className: "h-[18px] w-auto" },
  { src: amexLogo, label: "American Express", className: "h-[20px] w-auto" },
];

export function PaymentLogosRow() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5" aria-label={t("checkout.accepted_payment_methods")}>
      {PAYMENT_BRANDS.map((brand) => (
        <span key={brand.label} className={PILL_BASE} aria-label={brand.label}>
          <img src={brand.src} alt={brand.label} className={brand.className} decoding="async" />
        </span>
      ))}
    </div>
  );
}

/**
 * Keeps the Stripe Elements `amount` in sync with the React amount prop.
 * Required in deferred-intent mode whenever checkout pricing changes
 * (insurance, promo, climate offset, etc.).
 */
function ElementsAmountSync({ amount, currency }: { amount: number; currency: string }) {
  const elements = useElements();
  useEffect(() => {
    if (!elements) return;
    try {
      // Task #8092 (hygiene) — elements.update expects the smallest currency
      // unit, matching options.amount at mount. It also returns a Promise;
      // swallow rejections explicitly so an incompatible update doesn't
      // float as an unhandled rejection (the parent remounts via key=).
      void Promise.resolve(
        elements.update({ amount: Math.round(amount * 100), currency }),
      ).catch(() => {});
    } catch {
      /* Stripe will throw if the change is incompatible (e.g. mode change);
         the parent forces a remount via key= in that case. */
    }
  }, [elements, amount, currency]);
  return null;
}

/**
 * Stripe Elements provider configured for deferred-intent mode. Mode and
 * currency changes force a remount via `key`; amount changes propagate
 * through `elements.update()` so the renter never sees the form flash.
 */
export function PaymentElementsWrapper({
  stripePromise,
  amount,
  currency,
  mode,
  captureMethod = "automatic_async",
  resetKey = 0,
  children,
}: {
  stripePromise: Promise<Stripe | null> | null;
  amount: number;
  currency: string;
  mode: "payment" | "setup";
  /** Increment to force-remount the <Elements> provider (e.g. after a
   *  failed attempt so the next submit starts from a clean Stripe state). */
  resetKey?: number;
  /** Must mirror the server-side PaymentIntent.capture_method. Request
   *  bookings (non-instant) create the intent with `capture_method: "manual"`
   *  in api-server/src/routes/bookings.ts so the host can capture on
   *  approval; without this matching here, stripe.confirmPayment fails:
   *    "The provided capture_method (manual) does not match the expected
   *     capture_method (automatic). Try confirming with a Payment Intent
   *     that is configured to use the same parameters as Stripe Elements." */
  captureMethod?: "automatic" | "automatic_async" | "manual";
  children: React.ReactNode;
}) {
  // Task #3499: the currency here is the CHARGE currency the server
  // denominated the PaymentIntent in (`quote.chargeCurrency`, falling
  // back to `quote.currency`). Stripe enforces that the Elements
  // currency match the PaymentIntent currency exactly — initializing
  // Elements in the local listing currency while the server captured in
  // USD (partial-pay markets like NZ) made `confirmPayment` always fail:
  //   "The provided currency (usd) does not match the expected currency
  //    (nzd)."
  // We deliberately do NOT default to "USD" here: a guessed currency
  // would re-introduce the same class of mismatch. When the caller can't
  // resolve a charge currency we render the children outside an Elements
  // context (below) so the pay button stays disabled rather than
  // confirming against a fabricated currency.
  const currencyLower = (currency || "").trim().toLowerCase();
  // PHASE 0 (native embed) — when the native-app bridge is present the
  // payment is confirmed in the NATIVE Stripe payment sheet, so we must not
  // load Stripe.js / mount Elements inside the WebView at all. Null on the
  // real website → behavior unchanged.
  const nativeBridge = useNativeBridge();
  const options = useMemo<StripeElementsOptions>(() => {
    if (mode === "setup") {
      return {
        mode: "setup",
        currency: currencyLower,
        paymentMethodCreation: "manual",
        // NOTE: do NOT pass `paymentMethodTypes` here. The server-side
        // SetupIntent / PaymentIntent is created with
        // `automatic_payment_methods: { enabled: true, allow_redirects: "never" }`
        // (see api-server/src/routes/bookings.ts). Stripe enforces a strict
        // contract between the Elements config and the intent: if the
        // intent uses automatic_payment_methods then Elements must too.
        // Mixing the two raises the runtime error renters were seeing:
        //   "Payment details were collected through Stripe Elements using
        //    payment_method_types and cannot be confirmed through the API
        //    configured with automatic payment methods or
        //    allowed_payment_method_types."
        // Card-only filtering for this product is enforced via the Stripe
        // Dashboard's enabled payment methods + the explicit
        // ExpressCheckoutElement / Wallets config below, not via this list.
        appearance: { theme: "stripe" },
      };
    }
    return {
      mode: "payment",
      amount: Math.max(50, Math.round(amount * 100)),
      currency: currencyLower,
      paymentMethodCreation: "manual",
      // See setup-mode comment above — must NOT be set when the server
      // PaymentIntent uses `automatic_payment_methods`. Restricting to
      // card happens at the Dashboard level for this account.
      captureMethod,
      // Must match the server's PaymentIntent, which is created with
      // `setup_future_usage: "off_session"` in api-server/src/routes/bookings.ts
      // (the renter's card is saved off-session for request-booking captures
      // and host-approved post-trip
      // adjustments). Without this, stripe.confirmPayment fails with:
      //   "The provided setup_future_usage (off_session) does not match
      //    the expected setup_future_usage (null). Try confirming with a
      //    Payment Intent that is configured to use the same parameters
      //    as Stripe Elements."
      setupFutureUsage: "off_session",
      appearance: { theme: "stripe" },
    };
    // amount intentionally omitted — sync via ElementsAmountSync to avoid remount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, currencyLower, captureMethod]);
  // No publishable key (e.g. server didn't return one yet, dev environment
  // without Stripe configured) → render children outside an Elements
  // context. InlinePayButton tolerates this by gating on `useStripe()`
  // returning null; InlinePaymentSection skips its Stripe-only content.
  // Task #3499: same fallback when no charge currency could be resolved —
  // we must not mount Elements with an empty/guessed currency. Callers
  // also pass `stripeAvailable={false}` in this case so the pay button
  // renders as a disabled fallback (no useStripe/useElements calls) with
  // a "couldn't prepare payment, please retry" helper.
  if (nativeBridge || !stripePromise || !currencyLower) return <>{children}</>;
  return (
    <Elements
      // Task #8092 — captureMethod must be part of the key. On a custom-quote
      // checkout `captureMethod` flips manual → automatic_async when the host
      // quote loads; without a remount Stripe Elements silently keep the old
      // capture method and the first Apple Pay confirm fails with a
      // capture_method mismatch.
      key={`${mode}-${currencyLower}-${captureMethod}-${resetKey}`}
      stripe={stripePromise}
      options={options}
    >
      <ElementsCaptureContext.Provider value={mode === "payment" ? captureMethod : undefined}>
        <ElementsAmountSync amount={Math.max(0.5, amount)} currency={currencyLower} />
        {children}
      </ElementsCaptureContext.Provider>
    </Elements>
  );
}

type InlinePaymentSectionProps = {
  amount: number;
  mode: "payment" | "setup";
  savedCards: SavedCardLite[];
  selectedPaymentMethod: string;
  onSelectPaymentMethod: (id: string) => void;
  prepareBooking: (paymentMethodId?: string) => Promise<PrepareBookingResult>;
  onSuccess: (publicId: string) => void;
  onError: (msg: string, kind?: PaymentErrorKind) => void;
  /** Fast, AI-free keyword pre-check of the "message to host" note, forwarded
   *  to the Express Checkout (wallet) confirm so a tapped Apple/Google Pay runs
   *  the same pre-check as the card Pay button. Optional. */
  precheckNote?: () => Promise<boolean>;
  /** Remounts the Elements provider after a server block so the next attempt
   *  starts from a clean deferred-intent state — forwarded to the wallet
   *  confirm flow for parity with the card Pay button. */
  onElementsReset?: () => void;
  /** Toggles the page-level "Confirming…" overlay while the wallet confirm
   *  runs (prepareBooking → confirm). Mirrors the Pay button wiring. */
  onProcessingChange?: (processing: boolean) => void;
  /** H3 — displayed quote total in the local booking currency, compared
   *  against the server-confirmed total before the wallet charge runs. */
  displayedTotal?: number | null;
  deposit: number;
  depositCopyVariant?: DepositCopyVariant | null;
  currencySymbol: string;
  cardBrandLabel: (brand: string) => string;
  /** False when no publishable key is available (test envs, Stripe-disabled
   *  regions). The section then skips ExpressCheckoutElement / PaymentElement
   *  to avoid the Stripe runtime error about a missing Elements context. */
  stripeAvailable?: boolean;
  /** Task #1709 — fires whenever the renter edits the postal code field
   *  in the Stripe PaymentElement. The page-level checkout owns the
   *  debounce + /bookings/quote/tax refresh so the inline sales-tax line
   *  in the price breakdown can sharpen from the card's billing address.
   *  Empty string = field cleared. We rely on the change event rather
   *  than reading from Elements imperatively at submit so the renter sees
   *  the sharper estimate _before_ they confirm. */
  onBillingPostalCodeChange?: (postalCode: string) => void;
  /** Task #5038 — true while the feature-flags fetch is still in flight. The
   *  Express Checkout (Apple/Google Pay) wallet stays hidden until flags
   *  resolve so a wallet confirm can never call prepareBooking with a stale
   *  `false` payment-first-checkout flag (which would silently take the legacy
   *  create-trip-then-charge path and leave a visible trip if the charge then
   *  fails). prepareBooking also hard-aborts while loading as the backstop. */
  pfcFlagLoading?: boolean;
  /** Task #7819 — mirrors the card Pay button's `payButtonDisabled` flag.
   *  When true, the Express Checkout (Apple/Google Pay) wallet row shows a
   *  semi-transparent "price refreshing" overlay and any wallet onConfirm
   *  that fires before the flag clears (e.g. a very fast tap during a
   *  policy/date quote refresh) is immediately rejected via
   *  `event.paymentFailed()` so the sheet closes cleanly. */
  payButtonDisabled?: boolean;
  formBlocked?: boolean;
  /** US self-drive driver choice or alternate-email validation blocks all payment methods. */
  driverChoiceBlockMessage?: string | null;
  /** Per-checkout, consent-aware wallet telemetry context. The type forbids
   * financial, Stripe, wallet-account, and personal fields by construction. */
  walletAnalyticsContext?: WalletAnalyticsContext;
  paymentAnalyticsContext?: PaymentAnalyticsContext;
  /** Task #4687 — fires on every PaymentElement change with whether the card
   *  form now has any input (`!event.empty`). The page uses this to dismiss the
   *  "we cleared your card for security, re-enter it" notice the moment the
   *  renter starts re-entering their card after a moderation-block remount. */
  onCardFieldChange?: (hasInput: boolean) => void;
};

function CardRow({
  card,
  selected,
  onSelect,
  cardBrandLabel,
}: {
  card: SavedCardLite;
  selected: boolean;
  onSelect: () => void;
  cardBrandLabel: (b: string) => string;
}) {
  const { t } = useTranslation();
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors",
        selected ? "border-foreground bg-muted/30" : "border-border hover:bg-muted/50",
      )}
      data-testid={`saved-card-row-${card.id}`}
    >
      <input
        type="radio"
        checked={selected}
        onChange={onSelect}
        className="h-4 w-4"
        data-testid={`saved-card-radio-${card.id}`}
      />
      <CreditCard className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm font-medium">
        {cardBrandLabel(card.brand)} •••• {card.last4}
      </span>
      {card.isDefault && (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          {t("checkout.saved_card_default_badge")}
        </span>
      )}
      <span className="ml-auto text-xs text-muted-foreground">
        {t("checkout.saved_card_expiry_label", {
          month: String(card.expMonth).padStart(2, "0"),
          year: card.expYear,
        })}
      </span>
    </label>
  );
}

/**
 * Inline payment section. Renders the ExpressCheckoutElement at the top
 * always, then either:
 *  - saved-card radio rows (with a "use a different method" toggle that
 *    swaps to the PaymentElement), if the renter has region-eligible
 *    saved cards on file; or
 *  - the PaymentElement immediately, if no saved cards are available.
 *
 * Designed to live inside a <PaymentElementsWrapper> so useStripe /
 * useElements resolve. The actual confirm flow lives in <InlinePayButton>.
 */
export default function InlinePaymentSection({
  savedCards,
  selectedPaymentMethod,
  onSelectPaymentMethod,
  prepareBooking,
  onSuccess,
  onError,
  precheckNote,
  onElementsReset,
  onProcessingChange,
  displayedTotal,
  mode,
  deposit,
  depositCopyVariant,
  currencySymbol,
  cardBrandLabel,
  stripeAvailable = true,
  onBillingPostalCodeChange,
  onCardFieldChange,
  pfcFlagLoading = false,
  payButtonDisabled = false,
  formBlocked = false,
  driverChoiceBlockMessage = null,
  walletAnalyticsContext,
  paymentAnalyticsContext,
}: InlinePaymentSectionProps) {
  const { t } = useTranslation();
  // PHASE 0 (native embed) — present when this screen runs inside the
  // native app's DOM component. Payment then happens in the NATIVE Stripe
  // payment sheet (see NativeBridgePayButton), so the web PaymentElement /
  // ExpressCheckout UI is replaced by a short explainer. Null on the real
  // website → unchanged.
  const nativeBridge = useNativeBridge();
  const hasSavedCards = savedCards.length > 0;
  const isUsingNewCard = selectedPaymentMethod === "new" || !hasSavedCards;
  // Wallet availability is resolved asynchronously by the ExpressCheckoutElement
  // (onReady). We only render the wallet row — and the "or pay with card"
  // divider below it — once Stripe confirms a compatible wallet exists, so the
  // divider never dangles above a card form on devices with no Apple/Google Pay.
  const [walletAvailable, setWalletAvailable] = useState(false);
  // Task #5038 — keep the Express Checkout (Apple/Google Pay) wallet hidden
  // until the PFC feature flag has resolved. A wallet confirm tapped while
  // flags are loading would call prepareBooking with a stale `false`
  // payment-first-checkout flag and silently take the legacy
  // create-trip-then-charge path — exactly the failure this task removes.
  // A missing US driver choice is an actionable form error, not a loading
  // state. Do not put a message over Stripe's own wallet button: its label
  // shows through the overlay and neither message is readable.
  const showWallet = stripeAvailable && !nativeBridge && !pfcFlagLoading && !formBlocked && !driverChoiceBlockMessage;

  // When the renter has saved cards but explicitly opts into a different
  // method, we expand the PaymentElement below the radio rows so they can
  // pay with a brand-new card / wallet without leaving the page.
  const showPaymentElement = stripeAvailable && (!hasSavedCards || isUsingNewCard);

  if (nativeBridge) {
    return (
      <section
        className="rounded-xl border border-border p-5"
        data-testid="checkout-payment-section-native"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-base font-semibold text-foreground">
              {t("checkout.secure_payment_label")}
            </h2>
          </div>
          <PaymentLogosRow />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          {t(
            "checkout.native_payment_explainer",
            "Tap Pay to choose your payment method in the secure payment sheet. Apple Pay, Google Pay and cards are supported.",
          )}
        </p>
        {deposit > 0 && (
          <Alert variant="warning" icon={Shield} className="mt-4">
            <AlertDescription className="text-xs">
              <Trans
                i18nKey={depositCopyKey(depositCopyVariant ?? "generic", "explainer")}
                values={{ amount: `${currencySymbol}${deposit.toFixed(2)}` }}
                components={{ strong: <strong /> }}
              />
            </AlertDescription>
          </Alert>
        )}
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border border-border p-5"
      data-testid="checkout-payment-section"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">
            {t("checkout.secure_payment_label")}
          </h2>
        </div>
        <PaymentLogosRow />
      </div>

      {driverChoiceBlockMessage && (
        <div
          className="mt-4 rounded-lg border border-border bg-muted/40 p-3"
          data-testid="checkout-driver-choice-prompt"
        >
          <button
            type="button"
            data-testid="button-review-driver-choice"
            className="text-left text-sm font-medium text-foreground underline underline-offset-2 hover:text-foreground/80"
            onClick={() => {
              const target = document.getElementById("checkout-primary-driver-email")
                ?? document.getElementById("checkout-primary-driver-choice");
              target?.focus();
            }}
          >
            {driverChoiceBlockMessage}
          </button>
        </div>
      )}

      {/* Express Checkout (Apple Pay / Google Pay / Link). The element hides
          itself when no compatible wallet exists (onReady → walletAvailable),
          and its onConfirm runs the SAME confirm pipeline as the card Pay
          button (precheck → prepareBooking → H3 guard → confirm).
          Task #7819 — when payButtonDisabled is true (e.g. quote is refreshing
          after a policy switch) we overlay a semi-transparent spinner so the
          renter sees "price refreshing" rather than a button that silently fails.
          A missing driver choice instead hides the wallet and shows the
          actionable prompt above; it must not be overlaid on Stripe's button.
          The disabled prop also gates handleConfirm in ExpressCheckoutWithFallback
          so any wallet tap that fires before the quote resolves is immediately
          rejected via event.paymentFailed(), closing the Apple Pay sheet cleanly. */}
      {showWallet && (
        <div className={cn(walletAvailable && "mt-4")} data-testid="checkout-express-checkout">
          <div className="relative">
            <ExpressCheckoutWithFallback
              isSetup={mode === "setup"}
              prepareBooking={prepareBooking}
              precheckNote={precheckNote}
              onSuccess={onSuccess}
              onError={onError}
              onElementsReset={onElementsReset}
              onProcessingChange={onProcessingChange}
              displayedTotal={displayedTotal}
              onAvailabilityChange={setWalletAvailable}
              disabled={payButtonDisabled}
              walletAnalyticsContext={walletAnalyticsContext}
              paymentAnalyticsContext={paymentAnalyticsContext}
            />
            {payButtonDisabled && walletAvailable && (
              <div
                className="absolute inset-0 flex items-center justify-center rounded bg-background/60"
                aria-hidden="true"
                data-testid="checkout-wallet-refreshing-overlay"
              >
                <Spinner size="sm" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Divider only renders beneath an actually-rendered wallet row. */}
      {showWallet && walletAvailable && (
        <div className="my-3 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          <span>{t("checkout.or_pay_with_card")}</span>
          <span className="h-px flex-1 bg-border" />
        </div>
      )}

      {hasSavedCards && (
        <div className="space-y-2" data-testid="saved-cards-list">
          {savedCards.map((card) => (
            <CardRow
              key={card.id}
              card={card}
              selected={selectedPaymentMethod === card.id}
              onSelect={() => onSelectPaymentMethod(card.id)}
              cardBrandLabel={cardBrandLabel}
            />
          ))}
          <button
            type="button"
            onClick={() => onSelectPaymentMethod(isUsingNewCard ? (savedCards[0]?.id || "new") : "new")}
            className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            data-testid="checkout-use-different-method"
          >
            {t("checkout.use_different_method")}
          </button>
        </div>
      )}

      {showPaymentElement && (
        <div className={cn(hasSavedCards && "mt-3 border-t border-border pt-4")}>
          <PaymentElement
            options={{
              wallets: { link: "never" },
              // Task #1724: explicitly tell Stripe to render and collect
              // the postal-code field on the card form. The default
              // behaviour for the card payment method already shows
              // postal collection, but `fields.billingDetails.address`
              // makes it deterministic across Elements versions, so the
              // `onChange` event below reliably reports a `postalCode`
              // for the inline sales-tax refresh effect on
              // CheckoutPage.tsx. Without this, certain Stripe locales
              // / Elements versions silently dropped postal collection
              // and the tax row never sharpened.
              fields: {
                billingDetails: {
                  address: { postalCode: "auto", country: "auto" },
                },
              },
            }}
            onChange={(event) => {
              // Task #4687 — dismiss the "we cleared your card for security"
              // notice as soon as the renter starts re-entering card details
              // (`event.empty` is false once any field has input).
              onCardFieldChange?.(!event.empty);
              // Task #1709 — surface the postal code so the page can
              // refresh the inline sales-tax estimate from the card's
              // billing address. Stripe's typed event exposes
              // `value.billingDetails.address.postalCode` (camelCase) —
              // see @stripe/stripe-js StripePaymentElementChangeEvent.
              if (!onBillingPostalCodeChange) return;
              const postal = event.value?.billingDetails?.address?.postalCode ?? "";
              onBillingPostalCodeChange(postal || "");
            }}
          />
        </div>
      )}

      {deposit > 0 && (
        <Alert variant="warning" icon={Shield} className="mt-4">
          <AlertDescription className="text-xs">
            <Trans
              i18nKey={depositCopyKey(depositCopyVariant ?? "generic", "explainer")}
              values={{ amount: `${currencySymbol}${deposit.toFixed(2)}` }}
              components={{ strong: <strong /> }}
            />
          </AlertDescription>
        </Alert>
      )}
    </section>
  );
}

/**
 * ExpressCheckoutElement (Apple Pay / Google Pay / Link) wired to confirm
 * through the SAME `runConfirmPipeline` as the card Pay button. When the
 * renter taps a wallet, Stripe collects payment in its native sheet and then
 * fires `onConfirm`; we run precheck → elements.submit() → prepareBooking →
 * H3 authoritative-total guard → stripe.confirm* (status-gated) exactly like
 * the card flow. A wallet is always a fresh payment method, so the pipeline
 * runs in its new-method branch (usingSavedCard=false).
 *
 * Failure is fail-closed in BOTH directions: every failure routes through the
 * page's `onError` (releases the trip/holds + remounts Elements) AND calls
 * `event.paymentFailed()` so the wallet sheet shows the error / closes rather
 * than hanging open.
 *
 * The element self-hides when no compatible wallet is detected (`onReady`),
 * notifying the parent via `onAvailabilityChange` so the "or pay with card"
 * divider only renders when a wallet row is actually present.
 */
function ExpressCheckoutWithFallback({
  isSetup,
  prepareBooking,
  precheckNote,
  onSuccess,
  onError,
  onElementsReset,
  onProcessingChange,
  displayedTotal,
  onAvailabilityChange,
  disabled = false,
  walletAnalyticsContext,
  paymentAnalyticsContext,
}: {
  isSetup: boolean;
  prepareBooking: (paymentMethodId?: string) => Promise<PrepareBookingResult>;
  precheckNote?: () => Promise<boolean>;
  onSuccess: (publicId: string) => void;
  onError: (msg: string, kind?: PaymentErrorKind) => void;
  onElementsReset?: () => void;
  onProcessingChange?: (processing: boolean) => void;
  displayedTotal?: number | null;
  onAvailabilityChange: (available: boolean) => void;
  /** Task #7819 — when true, any wallet onConfirm that fires (e.g. a fast tap
   *  during a quote refresh) is immediately rejected via event.paymentFailed()
   *  so the Apple Pay / Google Pay sheet closes cleanly, and onError surfaces
   *  a brief "price is updating" message asking the renter to retry. */
  disabled?: boolean;
  walletAnalyticsContext?: WalletAnalyticsContext;
  paymentAnalyticsContext?: PaymentAnalyticsContext;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const expectedCaptureMethod = useContext(ElementsCaptureContext);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  // Stripe may replay an Express Checkout confirm callback while its sheet is
  // being torn down. paymentFailed() is scoped to the confirm event and Stripe
  // throws if it is called twice for that same event. Keep the guard outside
  // the pipeline so the disabled/gated path is protected too.
  const failedEventsRef = useRef<WeakSet<object>>(new WeakSet());
  const { confirmPriceChange, dialog: priceChangeDialog } = usePriceChangeConfirm();

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);
  const emitWalletLifecycle = useCallback((update: WalletLifecycleUpdate) => {
    if (!walletAnalyticsContext) return;
    track("checkout_wallet_lifecycle", { ...walletAnalyticsContext, ...update });
  }, [walletAnalyticsContext]);

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    const signalPaymentFailed = () => {
      const eventObject = event as unknown as object;
      if (failedEventsRef.current.has(eventObject)) return false;
      failedEventsRef.current.add(eventObject);
      try { event.paymentFailed({ reason: "fail" }); } catch { /* sheet gone */ }
      return true;
    };
    // Task #7819 — if the quote is still refreshing (policy/date change),
    // reject the wallet sheet immediately so the renter doesn't get charged
    // the stale amount. The overlay above makes this state visible, but a
    // very fast tap can still race through — this is the fail-closed backstop.
    if (disabled) {
      emitWalletLifecycle({
        stage: "blocked",
        outcome: "blocked",
        wallet_capability: "link_or_unknown",
        failure_category: walletAnalyticsContext?.quote_state === "refreshing" ? "quote_refresh" : "payment_gate",
      });
      if (!signalPaymentFailed()) return;
      onError(t("checkout.wallet_price_refreshing_retry", "Price is updating. Please try again in a moment."));
      return;
    }
    if (inFlightRef.current) return;
    emitWalletLifecycle({
      stage: "wallet_confirmed",
      outcome: "tapped",
      wallet_capability: "link_or_unknown",
    });
    inFlightRef.current = true;
    onProcessingChange?.(true);
    const paymentAttemptId = walletAnalyticsContext?.correlation_id || `wallet-${Math.random().toString(36).slice(2, 14)}`;
    const emitPaymentLifecycle = paymentAnalyticsContext ? (update: PaymentLifecycleUpdate) => {
      track("checkout_payment_lifecycle", {
        ...paymentAnalyticsContext, attempt_id: paymentAttemptId, method: "wallet", ...update,
      });
    } : undefined;
    emitPaymentLifecycle?.({ stage: "attempt", outcome: "started" });
    try {
      await runConfirmPipeline({
        stripe,
        elements,
        expectedCaptureMethod,
        t,
        precheckNote,
        prepareBooking,
        onSuccess: (publicId) => {
          emitPaymentLifecycle?.({ stage: "terminal_outcome", outcome: "succeeded" });
          onSuccess(publicId);
        },
        onError,
        onElementsReset,
        // Express Checkout Element is owned by this component. If preparation
        // remounts it, this attempt's component unmount cleanup flips the guard.
        isConfirmationElementMounted: () => mountedRef.current,
        displayedTotal,
        isSetup,
        // A wallet is always a fresh payment method — never a saved card.
        usingSavedCard: false,
        selectedPaymentMethod: "new",
        confirmPriceChange,
        onWalletLifecycle: emitWalletLifecycle,
        onPaymentLifecycle: emitPaymentLifecycle,
        // Tell the Express Checkout sheet the payment failed on every failure
        // branch so it surfaces the error / closes instead of hanging open.
        onWalletFailure: signalPaymentFailed,
      });
    } finally {
      onProcessingChange?.(false);
      inFlightRef.current = false;
    }
  };

  return (
    <>
      <ExpressCheckoutElement
        options={{
          paymentMethods: { applePay: "auto", googlePay: "auto", link: "auto", paypal: "never" },
        }}
        onReady={(e) => {
          // e.availablePaymentMethods is null until Stripe finishes detection;
          // treat null as "no wallet" so the row + divider stay hidden until a
          // compatible wallet is confirmed.
          const apm = e.availablePaymentMethods;
          const has = !!apm && (apm.applePay || apm.googlePay || apm.link);
          onAvailabilityChange(has);
          const wallet_capability =
            apm?.applePay && apm?.googlePay ? "multiple"
              : apm?.applePay ? "apple_pay"
                : apm?.googlePay ? "google_pay"
                  : apm?.link ? "link_or_unknown" : "none";
          emitWalletLifecycle({
            stage: "availability",
            outcome: has ? "available" : "unavailable",
            wallet_capability,
          });
        }}
        onConfirm={handleConfirm}
      />
      {priceChangeDialog}
    </>
  );
}

/**
 * Inline Pay button. Branches between saved-card (server-side
 * confirmCardPayment via the booking intent) and new-card / wallet
 * (client-side stripe.confirmPayment / confirmSetup with the deferred
 * Elements instance).
 */
type InlinePayButtonProps = {
  savedCards: SavedCardLite[];
  selectedPaymentMethod: string;
  prepareBooking: (paymentMethodId?: string) => Promise<PrepareBookingResult>;
  onSuccess: (publicId: string) => void;
  onError: (msg: string, kind?: PaymentErrorKind) => void;
  /** Task #1211 — fires whenever the Pay button toggles its in-flight
   *  state so the page-level booking modal can swap in the
   *  "Confirming…" overlay while Stripe runs (intent create →
   *  3DS → confirm → webhook). Optional; older call sites omit it. */
  onProcessingChange?: (processing: boolean) => void;
  payButtonLabel: string;
  payButtonDisabled?: boolean;
  payButtonHelperText?: string | null;
  /** Task #6144 — called when the renter taps/clicks the pay button while it
   *  is disabled by a USER-ACTIONABLE gate (phone verification, blocked
   *  message-to-host, delivery out of range, unresolved payment currency).
   *  The caller scrolls to + highlights the blocking section and captures the
   *  `checkout_pay_blocked_tap` analytics event. Omitted (undefined) when the
   *  disable is transient (quote updating, flags loading, booking in flight)
   *  so those taps stay inert. Wired via a wrapper element +
   *  `disabled:pointer-events-none` on the button so the button itself keeps
   *  its real `disabled` attribute and can never submit. */
  onDisabledTap?: () => void;
  /** Synchronous form guard; true means no payment attempt may start. */
  onValidateForm?: () => boolean;
  paymentAnalyticsContext?: PaymentAnalyticsContext;
  /** Fast, AI-free keyword pre-check of the "message to host" note. Called on
   *  click BEFORE any Stripe contact (`elements.submit()`); resolves true when
   *  the note is confidently bad (the hook shows the toast and we abort). The
   *  server's full AI gate at booking submit is the authoritative backstop, so
   *  this is optional — call sites that omit it simply skip the pre-check. */
  precheckNote?: () => Promise<boolean>;
  mode: "payment" | "setup";
  variant: "desktop" | "mobile" | "mobile-block";
  /** True while a transient price update is in flight (updatingQuote ||
   *  taxRecalcInFlight). Replaces the lock icon + label with a spinner +
   *  "Updating price…" and sets cursor-wait on the disabled button so the
   *  renter knows something is happening (not just a broken/blocked button). */
  transientLoading?: boolean;
  /** Called after a server block (message_to_owner_blocked / phone_unverified)
   *  that is already toasted by prepareBooking. The Elements provider must be
   *  remounted so the next attempt starts from a clean Stripe deferred-intent
   *  state — without this, confirmPayment returns "Invalid payment method"
   *  because the Elements are still locked from the prior elements.submit(). */
  onElementsReset?: () => void;
  /** H3 — the amount currently displayed to the renter in the price summary,
   *  in the local booking currency. Compared against the server-confirmed
   *  `PrepareBookingResult.serverTotal` before any Stripe confirm call. When
   *  the two differ by more than $0.50 the renter is asked to re-confirm the
   *  updated amount. Pass `undefined` to skip the check (e.g. in contexts
   *  where no quote is available). */
  displayedTotal?: number | null;
  /** Mirror of <PaymentElementsWrapper>'s `!!stripePromise`. When false the
   *  button still renders (so `data-testid="checkout-pay-button-desktop"`
   *  remains stable for callers that gate on disabled state — phone-verify
   *  tests, deliveryRangeError tests, etc.) but stays disabled and is a
   *  no-op on click. We render a different component to avoid calling the
   *  Stripe hooks (useStripe / useElements) outside an Elements provider —
   *  those hooks throw, not return null. */
  stripeAvailable?: boolean;
  /** PHASE 0 (native embed) — regional Stripe publishable key from the
   *  quote, forwarded to the native payment sheet when the native bridge
   *  is present. Ignored entirely on the real website. */
  stripePublishableKey?: string | null;
  /** PHASE 0 (native embed) — Stripe region slug from the quote (see
   *  resolveBookingStripeRegion). Ignored on the real website. */
  stripeRegion?: string | null;
};

export function InlinePayButton(props: InlinePayButtonProps) {
  // PHASE 0 (native embed) — when the native-app bridge is mounted the pay
  // action runs prepareBooking as usual but confirms in the NATIVE Stripe
  // payment sheet instead of web Elements. Null on the real website, where
  // the two pre-existing branches below are bit-for-bit unchanged.
  const nativeBridge = useNativeBridge();
  if (nativeBridge) {
    return <NativeBridgePayButton {...props} bridge={nativeBridge} />;
  }
  if (props.stripeAvailable === false) {
    return <FallbackPayButton {...props} />;
  }
  return <InlinePayButtonImpl {...props} />;
}

/**
 * PHASE 0 (native embed) — pay button used when `useNativeBridge()` is
 * non-null (the screen is embedded in the native app's DOM component).
 *
 * Flow mirrors FallbackPayButton's prepareBooking handling (same precheck,
 * same structured-error branches) but, instead of stopping at "Stripe isn't
 * ready", hands the freshly minted clientSecret to the native payment
 * sheet via the bridge and maps its result onto onSuccess/onError.
 *
 * Saved-card selection is intentionally IGNORED here (always a fresh
 * payment method): the native Payment Sheet collects/spends payment
 * methods itself, and confirming a server-attached saved PM is a separate
 * flow we are not wiring in Phase 0.
 */
function NativeBridgePayButton({
  bridge,
  payButtonLabel,
  variant,
  payButtonDisabled,
  payButtonHelperText,
  transientLoading,
  prepareBooking,
  precheckNote,
  onValidateForm,
  onSuccess,
  onError,
  onProcessingChange,
  onElementsReset,
  stripePublishableKey,
  stripeRegion,
}: InlinePayButtonProps & { bridge: NativeBridge }) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const inFlightRef = useRef(false);

  const handleClick = async () => {
    if (inFlightRef.current) return;
    if (onValidateForm?.()) return;
    inFlightRef.current = true;
    setProcessing(true);
    onProcessingChange?.(true);
    try {
      // Keyword pre-check the note before booking. The toast is owned by the
      // hook; a confident block aborts before prepareBooking.
      if (precheckNote && (await precheckNote())) return;
      let prep: PrepareBookingResult;
      try {
        prep = await prepareBooking(undefined);
      } catch (err) {
        if (isOccasionSelectionError(err)) {
          onError(err instanceof Error ? err.message : "__occasion_selection_required__");
          return;
        }
        // phone_unverified and message_to_owner_blocked are already handled
        // inside prepareBooking (correct toast + re-throw) — see
        // FallbackPayButton for the rationale.
        if (
          err instanceof BookingApiError &&
          (err.code === "phone_unverified" ||
            err.code === "message_to_owner_blocked" ||
            err.code === "dates_unavailable" ||
            err.code === "dates_conflict_hold" ||
            err.code === "dates_conflict_renter_own" ||
            err.code === "checkout_resume_pending" ||
            err.code === "checkout_resume_unavailable" ||
            isAvailabilityConflictError(err) ||
            // Task #7146 — own-already-booked retry: the hook already
            // navigated to the confirmed booking; never show an error banner.
            err.code === "dates_conflict_own_booked")
        ) {
          onElementsReset?.();
          return;
        }
        // BookingNoPaymentRequiredError and friends flow through onError;
        // handlePaymentError ignores the "__no_payment_required__" sentinel.
        onError(err instanceof Error ? err.message : t("checkout.payment_failed"),
          isPickupPolicyError(err) ? "location_verification" : undefined);
        return;
      }

      if (prep.clientSecret === "__no_payment_required__") {
        onSuccess(prep.publicId);
        return;
      }

      const result = await bridge.requestNativePayment({
        clientSecret: prep.clientSecret,
        publishableKey: stripePublishableKey || undefined,
        region: stripeRegion || undefined,
        intentType: prep.clientSecret.startsWith("seti_") ? "setup" : "payment",
      });
      if (result.ok) {
        onSuccess(prep.publicId);
        return;
      }
      // Cancel AND failure both route through onError: handlePaymentError
      // owns releasing the just-created trip/holds (abandonPayment), which
      // matters as much for a dismissed sheet as for a declined card.
      onError(
        result.message ||
          (result.canceled
            ? t("checkout.payment_canceled", "Payment cancelled.")
            : t("checkout.payment_failed")),
      );
    } finally {
      setProcessing(false);
      onProcessingChange?.(false);
      inFlightRef.current = false;
    }
  };

  const disabled = processing || !!payButtonDisabled;
  if (variant === "mobile") {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        data-testid="checkout-pay-button-mobile"
        className={cn(
          "primary-action touch-44 inline-flex items-center justify-center rounded-lg px-5 text-sm font-semibold",
          disabled && "opacity-50",
        )}
      >
        {processing ? <Spinner size="sm" /> : payButtonLabel}
      </button>
    );
  }
  if (variant === "mobile-block") {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          data-testid="checkout-pay-button-mobile-inline"
          className={cn(
            "primary-action w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold",
            disabled && "opacity-50",
          )}
        >
          {processing ? (
            <><Spinner size="sm" /> {t("common.processing")}</>
          ) : transientLoading ? (
            <><Spinner size="sm" /> {t("checkout.updating_price_label", "Updating price…")}</>
          ) : (
            <><Lock className="h-4 w-4" /> {payButtonLabel}</>
          )}
        </button>
        {/* This block-variant only renders below md (parent is md:hidden), so
            the helper text is shown unconditionally here — the desktop variant
            keeps its own `hidden md:block` copy. Without this, a greyed-out
            mobile Pay button gave no reason (Stripe.js not ready, currency
            unresolved, phone unverified, …). */}
        {payButtonHelperText && (
          <p
            data-testid="checkout-pay-button-helper-mobile"
            className="px-1 text-xs text-muted-foreground"
          >
            {payButtonHelperText}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className={cn("space-y-2", transientLoading && !processing && "cursor-wait")}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        data-testid="checkout-pay-button-desktop"
        className={cn(
          // Same md-gating as the web flows: the embedded WebView is a
          // narrow viewport, so it shows the mobile-block sibling instead
          // (CheckoutPaymentSection renders both) — no duplicate buttons.
          "primary-action hidden md:flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold",
          disabled && "opacity-50",
        )}
      >
        {processing ? (
          <><Spinner size="sm" /> {t("common.processing")}</>
        ) : transientLoading ? (
          <><Spinner size="sm" /> {t("checkout.updating_price_label", "Updating price…")}</>
        ) : (
          <><Lock className="h-4 w-4" /> {payButtonLabel}</>
        )}
      </button>
      {payButtonHelperText && (
        <p
          data-testid="checkout-pay-button-helper"
          className={cn("px-1 text-xs text-muted-foreground", transientLoading ? "block" : "hidden md:block")}
        >
          {payButtonHelperText}
        </p>
      )}
    </div>
  );
}

/**
 * Rendered when no Stripe publishable key is configured (test envs and
 * Stripe-disabled regions). The button still mounts so callers can assert
 * page-level form/validation state. On click we invoke `prepareBooking`
 * (server-side createBooking) so server-side validation paths
 * (phone_unverified, deliveryRangeError, etc.) can surface — the actual
 * stripe.confirm step is skipped because Stripe.js never loaded.
 *
 * The `disabled` state mirrors `payButtonDisabled` so that the existing
 * checkout test suites keep working without a real Stripe runtime.
 */
function FallbackPayButton({
  payButtonLabel,
  variant,
  payButtonDisabled,
  payButtonHelperText,
  transientLoading,
  onDisabledTap,
  onValidateForm,
  prepareBooking,
  precheckNote,
  onSuccess,
  onError,
  onProcessingChange,
  onElementsReset,
  selectedPaymentMethod,
  savedCards,
  paymentAnalyticsContext,
}: InlinePayButtonProps) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const inFlightRef = useRef(false);

  const usingSavedCard =
    selectedPaymentMethod !== "new" &&
    !!savedCards.find((c) => c.id === selectedPaymentMethod);

  const handleClick = async () => {
    if (inFlightRef.current) return;
    if (onValidateForm?.()) return;
    inFlightRef.current = true;
    const attemptId = `pay-${Math.random().toString(36).slice(2, 14)}`;
    const emitPaymentLifecycle = (update: PaymentLifecycleUpdate) => {
      if (!paymentAnalyticsContext) return;
      track("checkout_payment_lifecycle", {
        ...paymentAnalyticsContext, attempt_id: attemptId,
        method: usingSavedCard ? "saved_card" : "new_card", ...update,
      });
    };
    emitPaymentLifecycle({ stage: "attempt", outcome: "started" });
    setProcessing(true);
    onProcessingChange?.(true);
    try {
      // Keyword pre-check the note before booking. The toast is owned by the
      // hook; a confident block aborts before prepareBooking.
      if (precheckNote && (await precheckNote())) {
        emitPaymentLifecycle({ stage: "elements_validation", outcome: "blocked", failure_category: "precheck" });
        return;
      }
      try {
        const prep = await prepareBooking(usingSavedCard ? selectedPaymentMethod : undefined);
        // Without Stripe we can't confirm a clientSecret; only the
        // sentinel "no payment required" path is treated as success.
        if (prep.clientSecret === "__no_payment_required__") {
          emitPaymentLifecycle({ stage: "terminal_outcome", outcome: "succeeded" });
          onSuccess(prep.publicId);
        } else {
          emitPaymentLifecycle({ stage: "terminal_outcome", outcome: "failed", failure_category: "stripe_unavailable" });
          onError(t("checkout.stripe_not_ready"));
        }
      } catch (err) {
        if (isOccasionSelectionError(err)) {
          onError(err instanceof Error ? err.message : "__occasion_selection_required__");
          return;
        }
        // phone_unverified and message_to_owner_blocked are already handled
        // inside prepareBooking (correct toast + re-throw). Calling onError
        // on top of that would show a second inline banner redundantly.
        if (err instanceof BookingApiError &&
            (err.code === "phone_unverified" ||
              err.code === "message_to_owner_blocked" ||
              err.code === "dates_unavailable" ||
              err.code === "dates_conflict_hold" ||
              err.code === "dates_conflict_renter_own" ||
              err.code === "checkout_resume_pending" ||
              err.code === "checkout_resume_unavailable" ||
              isAvailabilityConflictError(err) ||
              // Task #7146 — hook already navigated to the confirmed booking.
              err.code === "dates_conflict_own_booked")) {
          // prepareBooking already toasted the reason. Remount Elements so the
          // next attempt gets a clean Stripe deferred-intent state.
          onElementsReset?.();
          return;
        }
        emitPaymentLifecycle({ stage: "terminal_outcome", outcome: "failed", failure_category: "booking_prepare" });
        onError(err instanceof Error ? err.message : t("checkout.payment_failed"),
          isPickupPolicyError(err) ? "location_verification" : undefined);
      }
    } finally {
      setProcessing(false);
      onProcessingChange?.(false);
      inFlightRef.current = false;
    }
  };

  const disabled = processing || !!payButtonDisabled;
  // Task #6144 — see InlinePayButtonImpl: disabled buttons swallow clicks, so
  // the tap-to-reveal interception lives on a wrapper element.
  const handleBlockedWrapperTap = () => {
    if (disabled && !processing) onDisabledTap?.();
  };
  if (variant === "mobile") {
    return (
      <span
        onClick={handleBlockedWrapperTap}
        data-testid="checkout-pay-blocked-catch-mobile"
        className="inline-flex"
      >
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          data-testid="checkout-pay-button-mobile"
          className={cn(
            "primary-action touch-44 inline-flex items-center justify-center rounded-lg px-5 text-sm font-semibold",
            disabled && "opacity-50 pointer-events-none",
          )}
        >
          {processing ? <Spinner size="sm" /> : payButtonLabel}
        </button>
      </span>
    );
  }
  if (variant === "mobile-block") {
    return (
      <div className="space-y-2">
        <div
          onClick={handleBlockedWrapperTap}
          data-testid="checkout-pay-blocked-catch-mobile-inline"
          className={cn(transientLoading && !processing && "cursor-wait")}
        >
          <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            data-testid="checkout-pay-button-mobile-inline"
            className={cn(
              "primary-action w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold",
              disabled && "opacity-50 pointer-events-none",
            )}
          >
            {processing ? (
              <><Spinner size="sm" /> {t("common.processing")}</>
            ) : transientLoading ? (
              <><Spinner size="sm" /> {t("checkout.updating_price_label", "Updating price…")}</>
            ) : (
              <><Lock className="h-4 w-4" /> {payButtonLabel}</>
            )}
          </button>
        </div>
        {payButtonHelperText && (
          <p
            data-testid="checkout-pay-button-helper-mobile"
            className="px-1 text-xs text-muted-foreground"
          >
            {payButtonHelperText}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div
        onClick={handleBlockedWrapperTap}
        data-testid="checkout-pay-blocked-catch-desktop"
        className={cn("hidden md:block", transientLoading && !processing && "cursor-wait")}
      >
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          data-testid="checkout-pay-button-desktop"
          className={cn(
            "primary-action flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold",
            disabled && "opacity-50 pointer-events-none",
          )}
        >
          {processing ? (
            <><Spinner size="sm" /> {t("common.processing")}</>
          ) : transientLoading ? (
            <><Spinner size="sm" /> {t("checkout.updating_price_label", "Updating price…")}</>
          ) : (
            <><Lock className="h-4 w-4" /> {payButtonLabel}</>
          )}
        </button>
      </div>
      {payButtonHelperText && (
        <p
          data-testid="checkout-pay-button-helper"
          className={cn("px-1 text-xs text-muted-foreground", transientLoading ? "block" : "hidden md:block")}
        >
          {payButtonHelperText}
        </p>
      )}
    </div>
  );
}

/**
 * Shared confirm pipeline used by BOTH the card Pay button
 * (<InlinePayButtonImpl>) and the Express Checkout (Apple Pay / Google Pay)
 * wallet row (<ExpressCheckoutWithFallback>). Centralizing it guarantees a
 * wallet tap runs the EXACT same safety sequence as the card button:
 *
 *   precheck note → elements.submit() (new-method only) → prepareBooking
 *   (server re-price) → H3 authoritative-total guard → stripe.confirm* with
 *   a post-confirm status gate.
 *
 * Failure handling is fail-closed: every early-return routes through `onError`
 * (which the page wires to `handlePaymentErrorWithReset` → releases the
 * just-created trip/holds via abandonPayment and remounts Elements). The
 * wallet path additionally passes `onWalletFailure`, invoked alongside every
 * failure so the Express Checkout sheet is told the payment failed (it then
 * shows the error / closes) instead of hanging open.
 */
/**
 * Task #4623 — promise-based, on-brand replacement for the native
 * window.confirm() that gated the H3 price-drift guard. The pipeline
 * (`runConfirmPipeline`) is a standalone async function shared by two
 * components, so it can't render UI itself. This hook hands it a
 * `confirmPriceChange(amount)` that resolves true/false once the user
 * answers the rendered `dialog`, preserving the original
 * continue/abort behaviour exactly.
 */
function usePriceChangeConfirm() {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<string | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setAmount(null);
    resolve?.(ok);
  }, []);

  const confirmPriceChange = useCallback((amt: string) => {
    setAmount(amt);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const dialog = (
    <AlertDialog
      open={amount !== null}
      onOpenChange={(open) => {
        if (!open) settle(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("checkout.price_changed_title", "Price updated")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {amount !== null ? t("checkout.price_changed_confirm", { amount }) : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settle(false)}>
            {t("checkout.price_changed_cancel", "Cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              settle(true);
            }}
            data-testid="checkout-price-changed-continue"
          >
            {t("checkout.price_changed_continue", "Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirmPriceChange, dialog };
}

export type ConfirmPipelineArgs = {
  stripe: Stripe | null;
  elements: StripeElements | null;
  expectedCaptureMethod?: CheckoutCaptureMethod;
  t: (key: string, options?: Record<string, unknown>) => string;
  precheckNote?: () => Promise<boolean>;
  prepareBooking: (paymentMethodId?: string) => Promise<PrepareBookingResult>;
  onSuccess: (publicId: string) => void;
  onError: (msg: string, kind?: PaymentErrorKind) => void;
  onElementsReset?: () => void;
  /** Returns false when the Stripe Element that started this attempt has
   * unmounted or been replaced while booking preparation was in flight. */
  isConfirmationElementMounted?: () => boolean;
  displayedTotal?: number | null;
  isSetup: boolean;
  usingSavedCard: boolean;
  selectedPaymentMethod: string;
  /** Wallet path only — signals the Express Checkout sheet that the payment
   *  failed so it surfaces an error / closes rather than hanging open. Called
   *  on EVERY failure branch (including the silently-toasted server blocks). */
  onWalletFailure?: () => void;
  /** Task #4623 — on-brand replacement for the native window.confirm() shown
   *  when the server re-prices the booking above the displayed quote. Resolves
   *  true to continue charging, false to abort. Falls back to window.confirm
   *  when not supplied. */
  confirmPriceChange?: (amount: string) => Promise<boolean>;
  /** Wallet-only lifecycle observer. This receives enum-only details; it is
   * deliberately absent from card paths so wallet analytics cannot mistake a
   * card confirmation for an Apple Pay/Google Pay journey. */
  onWalletLifecycle?: (update: WalletLifecycleUpdate) => void;
  onPaymentLifecycle?: (update: PaymentLifecycleUpdate) => void;
};

/**
 * Task #5983 — bound EVERY `stripe.confirm*` call so the checkout can never
 * park on the "Confirming your booking" overlay forever. Stripe.js can leave a
 * confirm promise pending indefinitely: an inline 3DS/SCA iframe that never
 * resolves, a wallet sheet that stalls, or the known internal "Could not
 * retrieve elements store" rejection (which fires on a SEPARATE unhandled
 * promise while the awaited confirm still hangs). Without a bound the awaited
 * confirm never settles, neither onSuccess nor fail() runs, and the overlay is
 * stuck.
 *
 * The window is deliberately generous (2 min) so a legitimately slow inline
 * SCA challenge — where the renter is fetching a bank OTP — is never cut short;
 * it only rescues the "hangs forever" case.
 */
export const STRIPE_CONFIRM_TIMEOUT_MS = 120_000;
export const STRIPE_CONFIRM_TIMEOUT = Symbol("stripe-confirm-timeout");

/**
 * `elements.submit()` is a client-only, pre-intent operation. Unlike Stripe
 * confirmation, it cannot have charged or pre-authorized anything yet, so a
 * permanently pending submission should return directly to the editable
 * retry state rather than enter server reconciliation.
 */
export const STRIPE_ELEMENTS_SUBMIT_TIMEOUT_MS = 30_000;
export const STRIPE_ELEMENTS_SUBMIT_TIMEOUT = Symbol("stripe-elements-submit-timeout");

export async function submitElementsWithWatchdog<T>(
  submit: () => Promise<T>,
): Promise<T | typeof STRIPE_ELEMENTS_SUBMIT_TIMEOUT> {
  const p = submit();
  // The watchdog deliberately abandons a hung Stripe.js promise. Consume a
  // late rejection so it cannot become an unhandled promise rejection after
  // the renter has already been returned to the retry form.
  p.catch(() => { /* swallowed — handled by the watchdog timeout below */ });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof STRIPE_ELEMENTS_SUBMIT_TIMEOUT>((resolve) => {
    timer = setTimeout(
      () => resolve(STRIPE_ELEMENTS_SUBMIT_TIMEOUT),
      STRIPE_ELEMENTS_SUBMIT_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([p, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function confirmWithWatchdog<T>(
  confirm: () => Promise<T>,
): Promise<T | typeof STRIPE_CONFIRM_TIMEOUT> {
  const p = confirm();
  // Guard the abandoned promise: if the watchdog wins the race, this confirm
  // is left dangling and may later reject (e.g. the Stripe.js "Could not
  // retrieve elements store" internal rejection — see
  // checkout-stripe-elements-store-transient). Attach a no-op catch so that
  // late rejection can never surface as an unhandled promise rejection.
  p.catch(() => { /* swallowed — handled by the watchdog timeout below */ });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof STRIPE_CONFIRM_TIMEOUT>((resolve) => {
    timer = setTimeout(() => resolve(STRIPE_CONFIRM_TIMEOUT), STRIPE_CONFIRM_TIMEOUT_MS);
  });
  try {
    return await Promise.race([p, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function runConfirmPipeline(args: ConfirmPipelineArgs): Promise<void> {
  // Task #6014 — recover deterministically from ANY thrown/rejected error inside
  // the confirm pipeline instead of re-throwing it.
  //
  // The re-throw here (added by #5995 only to make the value legible) escaped
  // handleClick — which awaits runConfirmPipeline in a try/FINALLY with NO catch
  // — so the throw both surfaced as an opaque unhandledrejection AND left the
  // page pinned on the "Confirming your booking" overlay forever: the overlay
  // is driven by bookingStep === "confirming", and onProcessingChange(false)
  // (fired in handleClick's finally) does NOT reset it — only onError
  // (→ handlePaymentError → setBookingStep("form")) or onSuccess do. Any
  // rejecting confirm therefore hung checkout indefinitely (Task #5983's
  // watchdog only rescues a LATE-RESOLVING confirm, never a rejecting one).
  //
  // The pipeline routes handled failures through fail()/onError and returns, so
  // only genuinely unexpected throws reach here. `progress.preparedPublicId`
  // tells us whether we got past prepareBooking (an intent + per-day calendar
  // holds now exist server-side); that decides the SAFE recovery.
  const progress: { preparedPublicId: string | null } = { preparedPublicId: null };
  try {
    return await runConfirmPipelineInner(args, progress);
  } catch (err) {
    if (isOccasionSelectionError(err)) {
      args.onWalletFailure?.();
      args.onError(err instanceof Error ? err.message : "__occasion_selection_required__");
      return;
    }
    // toRealError preserves message/name/stack (incl. non-Error values) so the
    // real cause is legible in Sentry rather than dropped behind the overlay.
    const real = toRealError(err);
    try {
      Sentry.captureException(real);
    } catch {
      /* reporting must never mask the recovery below */
    }
    if (progress.preparedPublicId) {
      args.onWalletLifecycle?.({
        stage: "watchdog_reconciliation",
        outcome: "reconciling",
        wallet_capability: "link_or_unknown",
        failure_category: "unexpected",
      });
      args.onPaymentLifecycle?.({ stage: "reconciliation", outcome: "reconciling", failure_category: "unexpected" });
      // Past prepareBooking: a pre-auth may be live server-side. Route to the
      // SAME honest server-reconcile path the confirm-timeout uses
      // (handlePaymentSuccess polls + reconciles, then lands the booking or
      // shows "still finalizing / only pre-authorized, don't resubmit"). Never
      // release holds on a possibly-live pre-auth (Task #5983 invariant).
      args.onSuccess(progress.preparedPublicId);
    } else {
      args.onWalletLifecycle?.({
        stage: "failure",
        outcome: "failed",
        wallet_capability: "link_or_unknown",
        failure_category: "unexpected",
      });
      args.onPaymentLifecycle?.({ stage: "terminal_outcome", outcome: "failed", failure_category: "unexpected" });
      // Threw before any intent/holds existed (e.g. precheck, elements.submit):
      // nothing to reconcile or orphan, so surface an honest error + let the
      // renter retry. Mirror fail(): close any wallet sheet first.
      args.onWalletFailure?.();
      args.onError(real.message || args.t("checkout.payment_failed"));
    }
  }
}

async function runConfirmPipelineInner(
  args: ConfirmPipelineArgs,
  progress: { preparedPublicId: string | null },
): Promise<void> {
  const {
    stripe,
    elements,
    t,
    precheckNote,
    prepareBooking,
    onSuccess,
    onError,
    onElementsReset,
    isConfirmationElementMounted,
    displayedTotal,
    isSetup,
    usingSavedCard,
    selectedPaymentMethod,
    onWalletFailure,
    confirmPriceChange,
    onWalletLifecycle,
    onPaymentLifecycle,
  } = args;

  // Route every failure through onError AND (on the wallet path) the
  // Express Checkout sheet, so the two flows never diverge on error handling.
  // `kind` lets the page banner pick calm vs alarming framing (Task #4687);
  // it defaults to "charge_failed" so existing call sites keep the red header.
  const fail = (
    msg: string,
    kind?: PaymentErrorKind,
    failureCategory: NonNullable<WalletLifecycleUpdate["failure_category"]> = "unexpected",
  ) => {
    onWalletLifecycle?.({
      stage: "failure",
      outcome: "failed",
      wallet_capability: "link_or_unknown",
      failure_category: failureCategory,
    });
    const paymentFailureCategory: NonNullable<PaymentLifecycleUpdate["failure_category"]> =
      failureCategory === "elements_submit" ? "incomplete_fields"
        : failureCategory === "quote_refresh" || failureCategory === "payment_gate" || failureCategory === "non_terminal_intent"
          ? "unexpected"
          : failureCategory;
    onPaymentLifecycle?.({ stage: "terminal_outcome", outcome: "failed", failure_category: paymentFailureCategory });
    onWalletFailure?.();
    onError(msg, kind);
  };

  // Keyword pre-check the note BEFORE any Stripe contact (the new-method path
  // calls elements.submit() below). The toast is owned by the hook; a
  // confident block aborts here so Stripe is never touched on a bad note.
  if (precheckNote && (await precheckNote())) {
    onPaymentLifecycle?.({ stage: "elements_validation", outcome: "blocked", failure_category: "precheck" });
    onWalletLifecycle?.({
      stage: "blocked",
      outcome: "blocked",
      wallet_capability: "link_or_unknown",
      failure_category: "precheck",
    });
    onWalletFailure?.();
    return;
  }
  if (!stripe) {
    fail(t("checkout.stripe_not_ready"), undefined, "stripe_unavailable");
    return;
  }

  // For new-method flow (new card OR wallet) the Elements must submit first
  // (deferred-intent). Saved-card flow doesn't touch Elements.
  if (!usingSavedCard) {
    onPaymentLifecycle?.({ stage: "elements_validation", outcome: "started" });
    if (!elements) {
      fail(t("checkout.stripe_not_ready"), undefined, "stripe_unavailable");
      return;
    }
    const submitRes = await submitElementsWithWatchdog(() => elements.submit());
    if (submitRes === STRIPE_ELEMENTS_SUBMIT_TIMEOUT) {
      // No prepareBooking call has happened, so no server intent/hold exists.
      // `fail` signals the wallet sheet (when present) and routes through the
      // page's onError, which clears the confirming overlay and remounts
      // Elements without releasing or reconciling a nonexistent booking.
      fail(
        t("checkout.stripe_not_ready"),
        "card_validation",
        "elements_submit",
      );
      return;
    }
    if (submitRes.error) {
      // Task #4687 — this is a client-side card-FIELD problem (incomplete /
      // invalid card details) caught before any charge is attempted. After a
      // moderation block remounts Elements the typed card is cleared, so this
      // fires with "card number incomplete". Classify it as "card_validation"
      // so the page shows a calm "check your card details" header instead of
      // the alarming "Payment Failed" one — while preserving Stripe's specific
      // field message text.
      fail(submitRes.error.message || t("checkout.check_card_details"), "card_validation", "elements_submit");
      return;
    }
  }

  let prep: PrepareBookingResult;
  try {
    onPaymentLifecycle?.({ stage: "booking_preparation", outcome: "started" });
    onWalletLifecycle?.({
      stage: "booking_preparation",
      outcome: "started",
      wallet_capability: "link_or_unknown",
    });
    prep = await prepareBooking(usingSavedCard ? selectedPaymentMethod : undefined);
  } catch (err) {
    if (err instanceof CheckoutContractChangedError) {
      fail(err.message, "card_validation", "booking_prepare");
      return;
    }
    if (isOccasionSelectionError(err)) {
      onWalletFailure?.();
      onError(err instanceof Error ? err.message : "__occasion_selection_required__");
      return;
    }
    // phone_unverified and message_to_owner_blocked are already handled inside
    // prepareBooking (correct toast + re-throw). Calling onError on top would
    // show a redundant second inline banner. We DO need to remount Elements so
    // the next attempt gets a clean deferred-intent state — without this,
    // confirmPayment returns "Invalid payment method" because the Elements
    // remain locked from the prior elements.submit().
    if (
      err instanceof BookingApiError &&
      (err.code === "phone_unverified" ||
        err.code === "message_to_owner_blocked" ||
        err.code === "dates_unavailable" ||
        err.code === "dates_conflict_hold" ||
        err.code === "dates_conflict_renter_own" ||
        err.code === "checkout_resume_pending" ||
        err.code === "checkout_resume_unavailable" ||
        isAvailabilityConflictError(err) ||
        // Task #7146 — own-already-booked retry: the hook already navigated
        // to the confirmed booking; never surface a "Payment Failed" banner.
        err.code === "dates_conflict_own_booked")
    ) {
      onPaymentLifecycle?.({ stage: "booking_preparation", outcome: "blocked", failure_category: "booking_prepare" });
      onWalletLifecycle?.({
        stage: "blocked",
        outcome: "blocked",
        wallet_capability: "link_or_unknown",
        failure_category: "booking_prepare",
      });
      onWalletFailure?.();
      onElementsReset?.();
      return;
    }
    const statusUnavailable = isCheckoutStatusUnavailable(err) ||
      (err instanceof Error && err.message === CHECKOUT_STATUS_UNAVAILABLE_MESSAGE);
    fail(statusUnavailable ? CHECKOUT_STATUS_UNAVAILABLE_MESSAGE : err instanceof Error ? err.message : t("checkout.payment_failed"),
      statusUnavailable ? "status_unavailable" : isPickupPolicyError(err) ? "location_verification" : undefined, "booking_prepare");
    return;
  }

  if (prep.clientSecret === "__no_payment_required__") {
    progress.preparedPublicId = prep.publicId;
    onPaymentLifecycle?.({ stage: "terminal_outcome", outcome: "succeeded" });
    onWalletLifecycle?.({
      stage: "terminal_outcome",
      outcome: "succeeded",
      wallet_capability: "link_or_unknown",
    });
    onSuccess(prep.publicId);
    return;
  }

  // Creation/preflight can return a different contract (including a resumed
  // intent). Refuse locally before any Stripe confirmation, in both directions.
  // Never change the PI's capture policy just to fit an already mounted form.
  if (!isSetup && !usingSavedCard && args.expectedCaptureMethod &&
      prep.captureMethod && args.expectedCaptureMethod !== prep.captureMethod) {
    fail(t("checkout.refresh_quote_capture_method", {
      defaultValue: "This quote changed while you were checking out. Refresh the quote and try again so your payment form can be updated.",
    }), "card_validation", "booking_prepare");
    return;
  }

  // H3 — authoritative total integrity check. The server re-prices the booking
  // at creation time; if the displayed quote has drifted (e.g. tax recalc,
  // promo expired, FX change) we must surface the discrepancy BEFORE the card
  // is charged. A $0.50 threshold absorbs floating-point rounding and tiny FX
  // wobble without false-positiving on real drifts.
  if (
    prep.serverTotal != null &&
    displayedTotal != null &&
    Math.abs(prep.serverTotal - displayedTotal) > 0.5
  ) {
    onPaymentLifecycle?.({ stage: "price_review", outcome: "started" });
    const amount = prep.serverTotal.toFixed(2);
    const ok = confirmPriceChange
      ? await confirmPriceChange(amount)
      : window.confirm(t("checkout.price_changed_confirm", { amount }));
    if (!ok) {
      onPaymentLifecycle?.({ stage: "price_review", outcome: "declined", failure_category: "price_changed" });
      fail(t("checkout.payment_failed"), undefined, "price_changed");
      return;
    }
    onPaymentLifecycle?.({ stage: "price_review", outcome: "accepted" });
  }

  const { clientSecret, publicId } = prep;
  // Task #6014 — we are now past prepareBooking: an intent + per-day calendar
  // holds exist server-side. Record it so the outer runConfirmPipeline catch
  // routes an unexpected throw to the honest server-reconcile path (never
  // releasing a possibly-live pre-auth) instead of surfacing a hard error.
  progress.preparedPublicId = publicId;
  onPaymentLifecycle?.({ stage: "booking_preparation", outcome: "prepared" });
  onWalletLifecycle?.({
    stage: "booking_preparation",
    outcome: "prepared",
    wallet_capability: "link_or_unknown",
  });
  const returnUrl = sanitizeCheckoutUrl(`${window.location.origin}${window.location.pathname}${window.location.search}`);

  // Booking preparation can update checkout state and remount the deferred
  // Elements tree. Never hand the old Elements instance to Stripe: Stripe.js
  // rejects it locally with "elements should have a mounted Payment Element or
  // Express Checkout Element". No confirmation has been sent at this point, so
  // return to a clean retry state rather than entering reconciliation.
  if (!usingSavedCard && isConfirmationElementMounted && !isConfirmationElementMounted()) {
    onElementsReset?.();
    fail(t("checkout.stripe_not_ready"), "card_validation", "stripe_unavailable");
    return;
  }

  // Task #5983 — watchdog recovery when a Stripe confirm hangs past the bound.
  // We cannot know locally whether the intent was pre-authorized or charged, so
  // route to the SAME honest server-reconcile path a normal success uses
  // (onSuccess → handlePaymentSuccess: polls for the materialized trip and
  // reconciles the checkout session, then either lands the renter on the booking
  // or shows the honest "still finalizing — you've only been pre-authorized, do
  // not resubmit" message). This dismisses the "Confirming your booking" overlay
  // AND lets the server backstop settle any pre-auth, so a retry can't
  // double-charge. Never fall through to a fail()/holds-release here — releasing
  // holds on a possibly-live pre-auth would free the calendar while funds are
  // reserved.
  const onConfirmTimeout = () => {
    onPaymentLifecycle?.({ stage: "reconciliation", outcome: "reconciling" });
    onWalletLifecycle?.({
      stage: "watchdog_reconciliation",
      outcome: "reconciling",
      wallet_capability: "link_or_unknown",
    });
    onSuccess(publicId);
  };
  const handleConfirmationError = (error: { message?: string } | undefined, fallback: string) => {
    if (!isExplicitTerminalStripeConfirmationError(error)) {
      onPaymentLifecycle?.({ stage: "reconciliation", outcome: "reconciling", failure_category: "unexpected" });
      // Stripe may have accepted confirmation even though its SDK could not
      // return a result. Treat API/connection/unknown errors like timeouts:
      // reconcile first and never call the failure-release path.
      onWalletLifecycle?.({
        stage: "watchdog_reconciliation",
        outcome: "reconciling",
        wallet_capability: "link_or_unknown",
        failure_category: "unexpected",
      });
      onSuccess(publicId);
      return;
    }
    fail(checkoutDeclineMessage(error, t), undefined, "stripe_confirm");
  };
  onWalletLifecycle?.({
    stage: "stripe_confirmation",
    outcome: "started",
    wallet_capability: "link_or_unknown",
  });
  onPaymentLifecycle?.({ stage: "stripe_confirmation", outcome: "confirming" });

  if (usingSavedCard) {
    // Server already attached the saved PM to the intent. We just need to call
    // confirmCardPayment / confirmCardSetup so 3DS can run.
    if (isSetup) {
      const res = await confirmWithWatchdog(() => stripe.confirmCardSetup(clientSecret));
      if (res === STRIPE_CONFIRM_TIMEOUT) return onConfirmTimeout();
      const { error, setupIntent } = res;
      if (error) {
        handleConfirmationError(error, t("checkout.authentication_failed"));
        return;
      }
      if (setupIntent && setupIntent.status !== "succeeded") {
        fail(t("checkout.payment_not_complete", { status: setupIntent.status }), undefined, "non_terminal_intent");
        return;
      }
    } else {
      const res = await confirmWithWatchdog(() => stripe.confirmCardPayment(clientSecret));
      if (res === STRIPE_CONFIRM_TIMEOUT) return onConfirmTimeout();
      const { error, paymentIntent } = res;
      if (error) {
        handleConfirmationError(error, t("checkout.authentication_failed"));
        return;
      }
      if (
        paymentIntent &&
        paymentIntent.status !== "succeeded" &&
        paymentIntent.status !== "requires_capture" &&
        paymentIntent.status !== "processing"
      ) {
        fail(t("checkout.payment_not_complete", { status: paymentIntent.status }), undefined, "non_terminal_intent");
        return;
      }
    }
  } else {
    // New card / wallet flow uses the Elements PaymentElement /
    // ExpressCheckoutElement (both confirm through the deferred Elements).
    if (isSetup) {
      const res = await confirmWithWatchdog(() =>
        stripe.confirmSetup({
          elements: elements!,
          clientSecret,
          confirmParams: { return_url: returnUrl },
          redirect: "if_required",
        }),
      );
      if (res === STRIPE_CONFIRM_TIMEOUT) return onConfirmTimeout();
      const { error } = res;
      if (error) {
        handleConfirmationError(error, t("checkout.payment_failed"));
        return;
      }
    } else {
      // C2 — capture the paymentIntent from confirmPayment to guard the success
      // path: resolving without an error does NOT guarantee the intent reached
      // a chargeable status. requires_action (e.g. inline SCA that couldn't
      // complete) resolves cleanly but leaves the card uncharged. Mirror the
      // status gate already on the saved-card path.
      const res = await confirmWithWatchdog(() =>
        stripe.confirmPayment({
          elements: elements!,
          clientSecret,
          confirmParams: { return_url: returnUrl },
          redirect: "if_required",
        }),
      );
      if (res === STRIPE_CONFIRM_TIMEOUT) return onConfirmTimeout();
      const { error, paymentIntent } = res;
      if (error) {
        handleConfirmationError(error, t("checkout.payment_failed"));
        return;
      }
      if (
        paymentIntent &&
        paymentIntent.status !== "succeeded" &&
        paymentIntent.status !== "requires_capture" &&
        paymentIntent.status !== "processing"
      ) {
        fail(t("checkout.payment_not_complete", { status: paymentIntent.status }), undefined, "non_terminal_intent");
        return;
      }
    }
  }

  onWalletLifecycle?.({
    stage: "terminal_outcome",
    outcome: "succeeded",
    wallet_capability: "link_or_unknown",
  });
  onSuccess(publicId);
}

function InlinePayButtonImpl({
  savedCards,
  selectedPaymentMethod,
  prepareBooking,
  precheckNote,
  onSuccess,
  onError,
  onProcessingChange,
  onElementsReset,
  payButtonLabel,
  payButtonDisabled,
  payButtonHelperText,
  transientLoading,
  onDisabledTap,
  onValidateForm,
  displayedTotal,
  mode,
  variant,
  paymentAnalyticsContext,
}: InlinePayButtonProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const expectedCaptureMethod = useContext(ElementsCaptureContext);
  const [processing, setProcessing] = useState(false);
  const inFlightRef = useRef(false);
  const { confirmPriceChange, dialog: priceChangeDialog } = usePriceChangeConfirm();
  const paymentAttemptIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const isSetup = mode === "setup";
  const usingSavedCard =
    selectedPaymentMethod !== "new" &&
    !!savedCards.find((c) => c.id === selectedPaymentMethod);

  const handleClick = async () => {
    if (inFlightRef.current) return;
    if (onValidateForm?.()) return;
    inFlightRef.current = true;
    const attemptId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID() : `pay-${Math.random().toString(36).slice(2, 14)}`;
    paymentAttemptIdRef.current = attemptId;
    const emitPaymentLifecycle = (update: PaymentLifecycleUpdate) => {
      if (!paymentAnalyticsContext) return;
      track("checkout_payment_lifecycle", {
        ...paymentAnalyticsContext, attempt_id: attemptId,
        method: usingSavedCard ? "saved_card" : "new_card", ...update,
      });
    };
    emitPaymentLifecycle({ stage: "attempt", outcome: "started" });
    setProcessing(true);
    onProcessingChange?.(true);
    try {
      // The full safety sequence (precheck → elements.submit → prepareBooking
      // → H3 total guard → confirm + status gate) lives in the shared
      // runConfirmPipeline so the wallet (Express Checkout) path runs the
      // identical flow. No onWalletFailure here — this is the card button.
      await runConfirmPipeline({
        stripe,
        elements,
        expectedCaptureMethod,
        t,
        precheckNote,
        prepareBooking,
        onSuccess: (publicId) => {
          emitPaymentLifecycle({ stage: "terminal_outcome", outcome: "succeeded" });
          onSuccess(publicId);
        },
        onError,
        onElementsReset,
        isConfirmationElementMounted: () => {
          if (!mountedRef.current || !elements) return false;
          // Older test doubles do not expose getElement. Real StripeElements
          // always does; preserve those focused tests while checking production.
          if (typeof elements.getElement !== "function") return true;
          try {
            return elements.getElement("payment") !== null;
          } catch {
            return false;
          }
        },
        displayedTotal,
        isSetup,
        usingSavedCard,
        selectedPaymentMethod,
        confirmPriceChange,
        onPaymentLifecycle: emitPaymentLifecycle,
      });
    } finally {
      setProcessing(false);
      onProcessingChange?.(false);
      inFlightRef.current = false;
    }
  };

  const disabled = processing || !!payButtonDisabled || !stripe;

  // Task #6144 — a disabled button swallows clicks (no event fires), so the
  // tap-to-reveal interception lives on a wrapper element. The button gets
  // `disabled:pointer-events-none` so the tap passes through to the wrapper,
  // which forwards it to onDisabledTap only while the button is disabled and
  // not processing (never while a confirm is in flight).
  const handleBlockedWrapperTap = () => {
    if (disabled && !processing) onDisabledTap?.();
  };

  if (variant === "mobile") {
    return (
      <>
        <span
          onClick={handleBlockedWrapperTap}
          data-testid="checkout-pay-blocked-catch-mobile"
          className="inline-flex"
        >
          <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            data-testid="checkout-pay-button-mobile"
            className="primary-action touch-44 inline-flex items-center justify-center rounded-lg px-5 text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none"
          >
            {processing ? <Spinner size="sm" /> : payButtonLabel}
          </button>
        </span>
        {priceChangeDialog}
      </>
    );
  }

  if (variant === "mobile-block") {
    return (
      <>
        <div className="space-y-2">
          <div
            onClick={handleBlockedWrapperTap}
            data-testid="checkout-pay-blocked-catch-mobile-inline"
            className={cn(transientLoading && !processing && "cursor-wait")}
          >
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled}
              data-testid="checkout-pay-button-mobile-inline"
              className="primary-action w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none"
            >
              {processing ? (
                <><Spinner size="sm" /> {t("common.processing")}</>
              ) : transientLoading ? (
                <><Spinner size="sm" /> {t("checkout.updating_price_label", "Updating price…")}</>
              ) : (
                <><Lock className="h-4 w-4" /> {payButtonLabel}</>
              )}
            </button>
          </div>
          {payButtonHelperText && (
            <p
              data-testid="checkout-pay-button-helper-mobile"
              className="px-1 text-xs text-muted-foreground"
            >
              {payButtonHelperText}
            </p>
          )}
        </div>
        {priceChangeDialog}
      </>
    );
  }

  return (
    <div className="space-y-2">
      <div
        onClick={handleBlockedWrapperTap}
        data-testid="checkout-pay-blocked-catch-desktop"
        className={cn("hidden md:block", transientLoading && !processing && "cursor-wait")}
      >
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          data-testid="checkout-pay-button-desktop"
          className="primary-action cursor-pointer flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none"
        >
          {processing ? (
            <>
              <Spinner size="sm" /> {t("common.processing")}
            </>
          ) : transientLoading ? (
            <>
              <Spinner size="sm" /> {t("checkout.updating_price_label", "Updating price…")}
            </>
          ) : (
            <>
              <Lock className="h-4 w-4" /> {payButtonLabel}
            </>
          )}
        </button>
      </div>
      {payButtonHelperText && (
        <p
          data-testid="checkout-pay-button-helper"
          className={cn("text-center text-xs text-muted-foreground", transientLoading ? "block" : "hidden md:block")}
        >
          {payButtonHelperText}
        </p>
      )}
      {priceChangeDialog}
    </div>
  );
}
